import { effect, EffectRef, inject, Injectable, Injector, runInInjectionContext, Signal } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';

import { AppValidators as availableValidators } from './validations';
import * as availableWhens from './whens';

import { toValuesSignal } from 'projects/client/src/app/utils/form';
import { DynamicDtoConfigDto } from 'shared/openapi/models/dynamic-dto-config-dto';
import { DynamicDtoConfigFieldDto } from 'shared/openapi/models/dynamic-dto-config-field-dto';
import { DynamicDtoConfigValidatorDto } from 'shared/openapi/models/dynamic-dto-config-validator-dto';
import { DynamicDtoConfigWhenDto } from 'shared/openapi/models/dynamic-dto-config-when-dto';
import { DynamicDtoValidatorNames } from 'shared/openapi/models/dynamic-dto-validator-names';
import { DynamicDtoWhenNames } from 'shared/openapi/models/dynamic-dto-when-names';
import { ProductType } from 'shared/openapi/models/product-type';

const fb = new FormBuilder().nonNullable;

@Injectable({
	providedIn: 'root',
})
export class DynamicFormBuilderService {
	private readonly injector = inject(Injector);

	build<T extends Record<keyof T, AbstractControl>>(
		config: DynamicDtoConfigDto,
		data?: DynamicDataRecord
	): DynamicForm<T> {
		return new DynamicForm<T>(this.injector, config, data);
	}
}

export class DynamicForm<T extends Record<keyof T, AbstractControl>> extends FormGroup<T> {
	private readonly formEffects = new Set<EffectRef>();
	private readonly tree: TreeField = { fields: {}, isArray: false, name: '', path: '' };
	private formValues!: Signal<object>;

	constructor(
		private readonly injector: Injector,
		config: DynamicDtoConfigDto,
		data?: DynamicDataRecord
	) {
		super({} as T);

		runInInjectionContext(this.injector, () => (this.formValues = toValuesSignal(this)));
		config.fields.sort((a, b) => a.name.localeCompare(b.name));
		this.buildTree(config.fields);
		Object.values(this.tree.fields).forEach((treeField) => this.buildControl(treeField, data));
		this.assignConfigProperties();
	}

	destroy() {
		this.formEffects.forEach((effectRef) => effectRef.destroy());
	}

	getValidator(path: string, validatorKey: string): DynamicDtoConfigValidatorDto | null {
		return this.getTreeField(path).validators?.find((validator) => validator.name === validatorKey) ?? null;
	}

	getTreeField(path: string): TreeField {
		const treeField = path
			.split('.')
			.reduce(
				(field: TreeField, pathPart) => (/^\d+/.test(pathPart) ? field : field.fields[pathPart]),
				this.tree
			);

		if (treeField) {
			return treeField;
		} else {
			throw new Error(`"${path}" does not exists in tree!`);
		}
	}

	pushArrayItem<TData = object>(name: string, data?: TData): AbstractControl {
		const formArray = this.get(name) as AbstractControl | null;

		if (formArray && formArray instanceof FormArray) {
			const treeField = this.getTreeField(name);
			const fields = Object.values(treeField.fields);

			if (fields.length) {
				// add FormGroup to array
				const group = fb.group({});

				fields.forEach((field) => {
					this.buildControl(field, data as Record<string, object>, group);
					this.assignConfigProperties(field, group.get(field.name)!);
				});
				formArray.push(group);

				return group;
			} else {
				// add FormControl to array
				const control = fb.control(data);

				this.assignConfigProperties(treeField, control);
				formArray.push(control);

				return control;
			}
		} else {
			throw Error(`FormArray with "${name}" name does not exist in DynamicFormGroup.`);
		}
	}

	private appendValidators(control: AbstractControl, treeField: TreeField) {
		// group validations by name and parameters, so we don't produce the same
		// error multiple times for every product type
		const groupedValidators: Record<
			string,
			DynamicDtoConfigValidatorDto & {
				productTypes: ProductType[];
			}
		> = {};

		treeField.validators?.forEach((validator) => {
			// pre-check validator
			if (!(validator.name in availableValidators)) {
				throw new Error(`Validator "${validator.name}" not exists!`);
			}

			const validatorGroup = [validator.name, ...validator.parameters].join();
			if (!groupedValidators[validatorGroup]) {
				groupedValidators[validatorGroup] = {
					...validator,
					productTypes: [validator.productType],
				};
			} else {
				groupedValidators[validatorGroup].productTypes.push(validator.productType);
			}
		});

		// create parametered validators
		const validators = Object.values(groupedValidators).map((validator) => {
			if (validator.parameters === null) {
				console.warn(`${treeField.path}'s validation ("${validator.name}") has null parameters!`);
			}

			// get the validation function
			const validationFunction = (
				availableValidators as unknown as Record<DynamicDtoValidatorNames, CallableFunction>
			)[validator.name];

			// create the validator with the product types and the parameters
			return validationFunction(validator.productTypes, this, ...validator.parameters);
		});

		// finally, add the prepared validators to the control
		control.addValidators(validators);
	}

	private appendWhens<TControl extends AbstractControl>(control: TControl, treeField: TreeField) {
		if (treeField.whens?.length) {
			const groupedWhens: Record<string, DynamicDtoConfigWhenDto[]> = {};

			treeField.whens.forEach((when) => {
				if (!groupedWhens[when.productType]) {
					groupedWhens[when.productType] = [];
				}

				groupedWhens[when.productType].push(when);
			});

			// create an effect that will handle the "when" rules
			const fieldWhenEffect = effect(
				() => {
					this.triggerWhenEffect(groupedWhens, control, treeField);
				},
				{ injector: this.injector }
			);

			this.triggerWhenEffect(groupedWhens, control, treeField);

			this.formEffects.add(fieldWhenEffect);
		}
	}

	private triggerWhenEffect(
		groupedWhens: Record<string, DynamicDtoConfigWhenDto[]>,
		control: AbstractControl,
		treeField: TreeField
	) {
		// get the current form values, just to trigger the effect when they change
		this.formValues();

		const shouldShowControl = Object.values(groupedWhens).some((whens) => {
			return whens.every((when) => {
				if (!(when.name in availableWhens)) {
					throw new Error(`When rule "${when.name}" not exists!`);
				}

				const whenFunction = (availableWhens as Record<DynamicDtoWhenNames, availableWhens.When>)[when.name];

				// call the "when" function with the form, the field name and the parameters
				// and return the result
				return whenFunction?.(control, this, treeField.path, ...when.parameters);
			});
		});

		const group = control.parent as FormGroup | null;

		// if the control should be shown and it's not in the group already, add it
		if (shouldShowControl && !group?.contains(treeField.name)) {
			group?.addControl(treeField.name, control);
		}
		// otherwise, if the control should not be shown and it's in the group, remove it
		if (!shouldShowControl && group?.contains(treeField.name)) {
			group?.removeControl(treeField.name);
		}
	}

	private assignConfigProperties(treeField: TreeField = this.tree, control: AbstractControl = this) {
		this.appendValidators(control, treeField);
		this.appendWhens(control, treeField);

		if (control instanceof FormGroup) {
			Object.entries(control.controls).forEach((entry) =>
				this.assignConfigProperties(treeField.fields[entry[0]], entry[1])
			);
		} else if (control instanceof FormArray) {
			control.controls.forEach((childControl) => {
				if (childControl instanceof FormGroup) {
					Object.entries(childControl.controls).forEach((entry) =>
						this.assignConfigProperties(treeField.fields[entry[0]], entry[1])
					);
				}
			});
		}
	}

	private buildControl(treeField: TreeField, data?: DynamicDataRecord, parent: FormGroup = this) {
		const { name } = treeField;
		const control = parent.get(name);
		data = data ?? {};

		if (!control) {
			const fields = Object.values(treeField.fields);

			if (treeField.isArray) {
				const items = (data[name] as []) ?? [];

				if (fields.length) {
					// add FormArray with FormGroups
					const formGroups = items.map(() => fb.group({}));

					parent.addControl(name, fb.array<FormGroup>(formGroups));
					formGroups.forEach((formGroup, index) =>
						fields.forEach((field) => this.buildControl(field, items[index], formGroup))
					);
				} else {
					// add FormArray with FormControls
					const controls = items.map((item) => fb.control(item));
					parent.addControl(name, fb.array<FormControl>(controls));
				}
			} else if (fields.length) {
				// add FormGroup
				const group = fb.group({});

				parent.addControl(name, group);
				fields.forEach((field) => this.buildControl(field, data[name] as Record<string, object>, group));
			} else {
				// add FormControl
				parent.addControl(name, fb.control(data[name]));
			}
		}
	}

	private buildTree(fields: DynamicDtoConfigFieldDto[]) {
		fields.forEach((field) => {
			const path = field.name.split('.');
			let parent = this.tree.fields;

			for (const [index, current] of path.entries()) {
				const isArray = current.endsWith('[]');
				const name = isArray ? current.slice(0, -2) : current;

				if (!parent[name]) {
					const treeField: TreeField = {
						fields: {},
						isArray,
						name,
						path: path
							.slice(0, index + 1)
							.join('.')
							.replace(/\[\d*]/g, ''),
					};

					// Don't allow to add validator and "when" arrays to parents
					if (index === path.length - 1) {
						treeField.validators = field.validators;
						treeField.whens = field.whens;
					}

					parent[name] = treeField;
				}

				parent = parent[name].fields;
			}
		});
	}
}

export type DynamicDataRecord = Record<string, object | null | undefined>;

export type TreeField = Partial<DynamicDtoConfigFieldDto> & {
	fields: Record<string, TreeField>;
	isArray: boolean;
	name: string;
	path: string;
};
